<template>
  <div class="flowbuilder-node-container" ref="containerEl">
    <BaseNode
      :node="node"
      :icon="nodeIcon"
      :metrics="metrics"
      :mini-metrics="true"
      @click="emit('click')"
      @delete-node="emit('delete-node')"
      :classes="nodeClasses">
      <template #context>Execute JavaScript code:</template>

      <div v-if="node.name" class="mt-3">{{ node.name }}</div>

      <div
        class="mt-2 font-mono text-sm"
        :class="[node.code ? 'opacity-50' : '']">
        {{ codePreview }}
      </div>

      <!-- Renders content badge -->
      <div
        v-if="rendersContent"
        class="mt-2.5 inline-flex items-center gap-x-1 rounded bg-blue-500/10 px-1.5 py-0.5"
        title="This code returns a value and will display it in the widget">
        <div class="text-xs">Renders content</div>
      </div>
    </BaseNode>
  </div>
</template>

<script setup lang="ts">
import { computed, toRefs, ref, inject } from 'vue'
import BaseNode from './BaseNode.vue'
import { getNodeIcon } from '../../utils/nodeIcons'
import { doesCodeReturnValue } from '../../services/flowUtilities'
import type { NodeCode } from '../../types/nodes'

/**
 * CodeNode Component
 *
 * Executes custom JavaScript code in the visitor's browser.
 *
 * Features:
 * - Execute arbitrary JavaScript
 * - Optional CSS selector to target specific elements
 * - Code preview in the flow builder
 * - Named code blocks for organization
 * - Badge indicator when code returns displayable content
 *
 * Props:
 * - node: The code node configuration with name, code, and selector
 * - icon: Optional custom icon
 * - CodeIcon: Default icon component for code nodes
 */

interface Props {
  node: NodeCode
  icon?: any
  CodeIcon: any
}

const props = defineProps<Props>()
const emit = defineEmits(['delete-node', 'click'])

const { node } = toRefs(props)
const containerEl = ref<HTMLElement>()

const getNodeMetrics = inject<(node: any) => any>('getNodeMetrics', () => null)

// Get the appropriate icon for this node
const nodeIcon = computed(() => {
  // If an icon is passed as a prop, use it
  if (props.icon) return props.icon

  // Otherwise use the mapped icon
  return getNodeIcon(props.node)
})

// Get metrics for this node
const metrics = computed(() => {
  return getNodeMetrics(props.node)
})

/**
 * Check if code returns a value for display
 * Uses safe execution to detect return statements
 */
const rendersContent = computed(() => {
  return doesCodeReturnValue(node.value.code)
})

/**
 * Node CSS classes - includes does-terminate when code returns content
 */
const nodeClasses = computed(() => {
  const baseClasses = 'bg-lime-100 ring-lime-400/50 dark:bg-lime-950 dark:ring-lime-500/50'
  return rendersContent.value ? `${baseClasses} does-terminate` : baseClasses
})

/**
 * Generate a preview of the code for display
 * Truncates long code snippets for readability
 */
const codePreview = computed(() => {
  return node.value.code
    ? node.value.code.length > 30
      ? node.value.code.slice(0, 30) + '...'
      : node.value.code
    : "console.log('Hello, world!')"
})
</script>

